import initSqlJs from 'sql.js';
import * as XLSX from 'xlsx';
import { runBody } from '../Utils';
import { api } from '../TokenManager';
import wasmBinary from 'sql.js/dist/sql-wasm.wasm'; 

let SQLModule = null;

const getSQLModule = async () => {
  if (SQLModule) return SQLModule;
  
  SQLModule = await initSqlJs({
    locateFile: () => wasmBinary
  });
  return SQLModule;
};
/*
let SQLModule = null;

const getSQLModule = async () => {
  if (SQLModule) return SQLModule;
  
  SQLModule = await initSqlJs({
    // Fetch directly from the root public static folder
    locateFile: (file) => `${process.env.PUBLIC_URL || ''}/${file}`
  });
  return SQLModule;
};*/

// Create a FRESH database instance per upload operation to prevent memory leakage
const createFreshDB = async () => {
  const SQL = await getSQLModule();
  return new SQL.Database();
};

const sanitize = (name) => name.toString().replace(/[^a-zA-Z0-9_]/g, '_');

const ensureDimensionsTable = async (database) => {
  const tableExists = database.exec("SELECT name FROM sqlite_master WHERE type='table' AND name='dimensions_of_items'");
  if (tableExists.length > 0) return;

  try {
    const response = await fetch('/Dimensions_sample.xlsx');
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    const sheetName = workbook.SheetNames.find(n => n.includes('Packing List')) || workbook.SheetNames[0];
    const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

    if (data.length === 0) return;

    const keys = Object.keys(data[0]);
    const safeCols = keys.map(sanitize);

    database.run(`CREATE TABLE dimensions_of_items (${safeCols.map(c => `${c} TEXT`).join(', ')})`);

    const insertSql = `INSERT INTO dimensions_of_items VALUES (${safeCols.map(() => '?').join(',')})`;
    const stmt = database.prepare(insertSql);
    data.forEach(row => {
      stmt.run(keys.map(k => row[k] || ""));
    });
    stmt.free();
  } catch (err) {
    console.error("Failed to load permanent dimensions file from assets:", err);
  }
};

export const saveExcelDataForEditing = async (file, otherFiles = [], packoutName, sessionId, callbackOrOrders) => {
  // Always create a clean DB for this upload session
  const database = await createFreshDB();

  try {
    await ensureDimensionsTable(database);

    let callback = null;
    let customLoadOrders = [];

    if (typeof callbackOrOrders === 'function') {
      callback = callbackOrOrders;
    } else if (Array.isArray(callbackOrOrders)) {
      customLoadOrders = callbackOrOrders;
    }

    if (callback) callback();

    const totalFiles = [file, ...otherFiles];
    let formattedData = [];
    let globalIndex = 1;

    for (let fileIndex = 0; fileIndex < totalFiles.length; fileIndex++) {
      const currentFile = totalFiles[fileIndex];
      const currentLoadOrder = customLoadOrders[fileIndex] || (fileIndex + 1);

      await new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet);

            if (rows.length === 0) return resolve();

            const originalHeaders = Object.keys(rows[0]);
            const niinHeader = originalHeaders.find(h => h.toUpperCase().includes('NIIN'));
            const nsnHeader = originalHeaders.find(h => h.toUpperCase().includes('NSN'));

            let headerType = 0;
            if (!niinHeader) {
              headerType = 1;
              if (!nsnHeader) {
                console.error(`Upload failed: No NSN column found in file ${currentFile.name}`);
                return resolve();
              }
            }

            const safeHeaders = originalHeaders.map(sanitize);
            database.run(`DROP TABLE IF EXISTS parsed_user_data`);
            database.run(`CREATE TABLE parsed_user_data (${safeHeaders.map(h => `${h} TEXT`).join(', ')})`);

            const userStmt = database.prepare(`INSERT INTO parsed_user_data VALUES (${safeHeaders.map(() => '?').join(',')})`);
            rows.forEach(row => userStmt.run(originalHeaders.map(h => row[h] || "")));
            userStmt.free();

            database.run(`DROP TABLE IF EXISTS parsed_with_dimensions`);
            const userNsnCol = headerType === 1 ? sanitize(nsnHeader) : sanitize(niinHeader);
            const dimensionCol = headerType === 1 ? 'NSN' : 'NIIN';

            database.run(`
              CREATE TABLE parsed_with_dimensions AS 
              SELECT u.*, d.* FROM parsed_user_data u
              INNER JOIN dimensions_of_items d ON u.${userNsnCol} = d.${dimensionCol}
            `);

            const query = database.prepare("SELECT * FROM parsed_with_dimensions");
            const cleanStr = (val) => (val ? String(val).trim() : "");

            while (query.step()) {
              const row = query.getAsObject();

              formattedData.push({
                id: globalIndex,
                name: cleanStr(row.Noun || row.Nomenclature || row.Noun___Nomenclature || row.NOMENCLATURE),
                nsn: cleanStr(row.NSN || row.National_Stock_Number__NSN_),
                niin: parseFloat(row.NIIN) || cleanStr(row.NIIN),
                nomenclature: cleanStr(row.Noun || row.Nomenclature || row.NOMENCLATURE),
                weight: parseFloat(row.Unit_Packed_Weight || row.unit_pk_wgt || 0),
                length: parseFloat(row.Unit_Packed_Length || row.unit_pk_length || 0),
                width: parseFloat(row.Unit_Packed_Width || row.unit_pk_width || 0),
                height: parseFloat(row.Unit_Packed_Depth || row.unit_pk_depth || 0),
                authorizedqty: parseFloat(row.Authorized_Qty || row.Auth_Qty || 0),
                qty: parseFloat(row.On_Hand_Qty || row.Qty_on_Hand || row.On_Hand_Balance || 0),
                storagetype: cleanStr(row.Storage_Type),
                maxsupportedweight: 0,
                loadorder: currentLoadOrder,
                ksn: cleanStr(row.Kit_Serial_Number__KSN_ || row.KSN || row.ksn || row.Kit_Serial_Number),
                shopcode: cleanStr(row.shopcode || row.shop_code || row.Shop_Code || row.Shop_code),
                location: cleanStr(row.location || row.Location),
                runid: sessionId,
                packoutname: packoutName
              });

              globalIndex++;
            }
            query.free();
            resolve();
          } catch (err) {
            reject(err);
          }
        };

        reader.onerror = (err) => reject(err);
        reader.readAsArrayBuffer(currentFile);
      });
    }

    const rootPayload = runBody({ packoutName, boxes: formattedData }, sessionId);
    const response = await api.post(`/api/boxesForEdit`, rootPayload);
    console.log("API Success:", response.data);

  } finally {
    // Crucial: Free WebAssembly database memory after completion
    database.close();
  }
};